import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * Global store for scroll positions across different routes
 * Maps route keys to scroll positions for restoration
 * @type {Object.<string, number>}
 */
const scrollMemory = {};

/**
 * ScrollManager Component
 *
 * Utility component that manages scroll position restoration across route changes.
 * Automatically saves and restores scroll positions when navigating between pages,
 * providing a smooth user experience similar to native browser behavior.
 *
 * Features:
 * - Saves scroll position for each unique route
 * - Restores position on browser back/forward navigation
 * - Supports explicit scroll preservation via location.state.preserveScroll
 * - Scrolls to top on new page navigation by default
 * - Debounced scroll saving for performance
 *
 * @component
 *
 * @example
 * // Add to your main App component
 * import ScrollManager from './utils/ScrollManager';
 *
 * function App() {
 *   return (
 *     <>
 *       <ScrollManager />
 *       <Routes>
 *         {/* Your routes *\/}
 *       </Routes>
 *     </>
 *   );
 * }
 *
 * @example
 * // Navigate with preserved scroll position
 * navigate('/some-page', { state: { preserveScroll: true } });
 *
 * @returns {null} This component renders nothing (no UI)
 */
const ScrollManager = () => {
  const location = useLocation();

  useEffect(() => {
    /**
     * Create a unique key for this route
     * Includes pathname, search params, and hash for precise identification
     */
    const currentKey = location.pathname + location.search + location.hash;

    /**
     * Check if we're navigating back using browser buttons
     * Uses Performance API to detect back/forward navigation
     */
    const navigationType = window.performance?.getEntriesByType?.('navigation')?.[0]?.type;
    const isBackForward = navigationType === 'back_forward';

    /**
     * Get saved scroll position for this route (if any)
     */
    const savedPosition = scrollMemory[currentKey];

    // Determine scroll behavior based on navigation type
    if (isBackForward && savedPosition !== undefined) {
      /**
       * User is navigating with browser buttons - restore exact position
       * Uses requestAnimationFrame to ensure DOM is ready
       */
      requestAnimationFrame(() => {
        window.scrollTo({
          top: savedPosition,
          left: 0,
          behavior: 'instant'
        });
      });
    } else if (savedPosition !== undefined && location.state?.preserveScroll) {
      /**
       * Explicitly told to preserve scroll (e.g., returning from detail to list)
       * Parent component can request scroll preservation via location.state
       */
      requestAnimationFrame(() => {
        window.scrollTo({
          top: savedPosition,
          left: 0,
          behavior: 'instant'
        });
      });
    } else {
      /**
       * New navigation - scroll to top
       * Default behavior for fresh page visits
       */
      window.scrollTo({
        top: 0,
        left: 0,
        behavior: 'instant'
      });
    }

    /**
     * Save scroll position continuously as user scrolls
     * Uses debouncing to avoid excessive saves
     */
    let scrollTimeout;
    const saveScrollPosition = () => {
      clearTimeout(scrollTimeout);
      scrollTimeout = setTimeout(() => {
        scrollMemory[currentKey] = window.scrollY;
      }, 100); // Debounce to avoid too many saves
    };

    // Listen to scroll events
    window.addEventListener('scroll', saveScrollPosition, { passive: true });

    /**
     * Cleanup function - Save position when leaving the page
     * Ensures final scroll position is captured immediately
     */
    return () => {
      window.removeEventListener('scroll', saveScrollPosition);
      // Save final position immediately (no debounce)
      scrollMemory[currentKey] = window.scrollY;
    };
  }, [location]);

  // This component doesn't render anything
  return null;
};

export default ScrollManager;
